import { useQuery } from "react-query";
import { collection, getDocs, doc, getDoc } from "firebase/firestore";
import { db } from "../services/firebase";
import { TopicStatsCategory } from "../types";

/** トピックが最も最近出された週（今週=1, 先週=2, ...4週前=4, それ以前=null） */
export type WeekAgo = 1 | 2 | 3 | 4 | null;

interface TopicWeeklyInfo {
  count: number;
  latestWeekAgo: WeekAgo;
}

interface TopicStatsResponse {
  stats: { [genre: string]: { [difficulty: number]: TopicWeeklyInfo } };
  byCategory: {
    [category in TopicStatsCategory]: {
      [genre: string]: { [difficulty: number]: TopicWeeklyInfo };
    };
  };
  meta?: { totalDocuments: number; validDocuments: number };
}

const isNonEmptyString = (value: unknown): boolean =>
  typeof value === "string" && value.trim() !== "";

/**
 * topic の登録状況カテゴリを判定する（アプリのホーム振り分け・CSV出力の
 * export-topics-master と同じ考え方）。
 * - 課題文（sourceText）が非空 → claimWithSourceText
 * - questionType が preference / openEnded → preference
 * - questionType が claim → claim
 * - それ以外（未設定など）→ unknown
 */
const resolveTopicStatsCategory = (data: Record<string, unknown>): TopicStatsCategory => {
  if (isNonEmptyString(data.sourceText)) {
    return "claimWithSourceText";
  }
  const questionType = data.questionType;
  if (questionType === "preference" || questionType === "openEnded") {
    return "preference";
  }
  if (questionType === "claim") {
    return "claim";
  }
  return "unknown";
};

/**
 * ISO週番号を計算する（月曜日始まり）
 */
const getISOWeekNumber = (date: Date): { year: number; week: number } => {
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  d.setUTCDate(d.getUTCDate() + 4 - (d.getUTCDay() || 7));
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  const weekNumber = Math.ceil(((d.getTime() - yearStart.getTime()) / 86400000 + 1) / 7);
  return { year: d.getUTCFullYear(), week: weekNumber };
};

/**
 * 週の開始日（月曜日）を取得
 */
const getWeekStartDate = (date: Date): Date => {
  const d = new Date(date);
  const day = d.getDay();
  const diff = d.getDate() - day + (day === 0 ? -6 : 1);
  d.setDate(diff);
  d.setHours(0, 0, 0, 0);
  return d;
};

/**
 * 週IDを生成（例: "2024-W03"）
 */
const generateWeekId = (date: Date): string => {
  const { year, week } = getISOWeekNumber(date);
  return `${year}-W${week.toString().padStart(2, "0")}`;
};

/**
 * 直近4週分の週IDを生成（今週=1週目, 先週=2週目, ...）
 */
const getRecentWeekIds = (): { weekId: string; weekAgo: 1 | 2 | 3 | 4 }[] => {
  const today = new Date();
  const currentWeekStart = getWeekStartDate(today);

  return [1, 2, 3, 4].map((weekAgo) => {
    const weekStart = new Date(currentWeekStart);
    weekStart.setDate(currentWeekStart.getDate() - (weekAgo - 1) * 7);
    return {
      weekId: generateWeekId(weekStart),
      weekAgo: weekAgo as 1 | 2 | 3 | 4,
    };
  });
};

/**
 * Firestore から問題統計を取得（weeklyTopics連携）
 */
const fetchTopicStatsFromFirestore = async (): Promise<TopicStatsResponse> => {
  // 1. 直近4週分のweeklyTopicsを取得
  const recentWeeks = getRecentWeekIds();
  const weeklyDocs = await Promise.all(
    recentWeeks.map(({ weekId }) => getDoc(doc(db, "weeklyTopics", weekId)))
  );

  // topicId → 最も最近の weekAgo をマッピング
  const topicLatestWeekAgo = new Map<string, 1 | 2 | 3 | 4>();
  // weekAgoが小さい（最近）ほうが優先なので、大きいほうから処理
  for (let i = recentWeeks.length - 1; i >= 0; i--) {
    const docSnap = weeklyDocs[i];
    if (docSnap.exists()) {
      const topicIds = (docSnap.data().topicIds || []) as string[];
      for (const topicId of topicIds) {
        topicLatestWeekAgo.set(topicId, recentWeeks[i].weekAgo);
      }
    }
  }

  // 2. 全トピックを取得
  const snapshot = await getDocs(collection(db, "topics"));

  const stats: { [genre: string]: { [difficulty: number]: TopicWeeklyInfo } } = {};
  const byCategory: TopicStatsResponse["byCategory"] = {
    preference: {},
    claim: {},
    claimWithSourceText: {},
    unknown: {},
  };
  let totalCount = 0;
  let filteredCount = 0;

  snapshot.docs.forEach((docSnap) => {
    const data = docSnap.data();
    totalCount++;

    if (data.isPublished !== true) {
      return;
    }

    const genre = data.genre;
    const difficulty = data.difficulty;

    if (!genre || typeof difficulty !== "number" || difficulty < 1 || difficulty > 5) {
      console.warn(`Invalid data in document ${docSnap.id}:`, { genre, difficulty });
      return;
    }

    filteredCount++;

    const weekAgo: WeekAgo = topicLatestWeekAgo.get(docSnap.id) || null;

    // 全体統計を更新
    if (!stats[genre]) stats[genre] = {};
    if (!stats[genre][difficulty]) {
      stats[genre][difficulty] = { count: 0, latestWeekAgo: null };
    }
    stats[genre][difficulty].count++;
    if (weekAgo !== null) {
      const current = stats[genre][difficulty].latestWeekAgo;
      if (current === null || weekAgo < current) {
        stats[genre][difficulty].latestWeekAgo = weekAgo;
      }
    }

    // カテゴリ別統計（思いを伝える / 主張を伝える / 課題文あり / 未設定）
    const category = resolveTopicStatsCategory(data);

    if (!byCategory[category][genre]) byCategory[category][genre] = {};
    if (!byCategory[category][genre][difficulty]) {
      byCategory[category][genre][difficulty] = { count: 0, latestWeekAgo: null };
    }
    byCategory[category][genre][difficulty].count++;
    if (weekAgo !== null) {
      const current = byCategory[category][genre][difficulty].latestWeekAgo;
      if (current === null || weekAgo < current) {
        byCategory[category][genre][difficulty].latestWeekAgo = weekAgo;
      }
    }
  });

  return {
    stats,
    byCategory,
    meta: {
      totalDocuments: totalCount,
      validDocuments: filteredCount,
    },
  };
};

export const useTopicStats = () => {
  return useQuery("topicStats", fetchTopicStatsFromFirestore, {
    staleTime: 10 * 60 * 1000,
    cacheTime: 2 * 60 * 1000,
    refetchOnWindowFocus: true,
  });
};
